/**
 * `logs.v1` — wire → canonical decoder for Logs-shaped cells.
 *
 * Sync-log entries appended to provider rows (e.g. weather) to surface
 * sync activity on the row. The generated `SheetsNetwork.LogsCell` model
 * hydrates the `at` timestamp and validates `{ at, level, message,
 * source? }`.
 *
 * Null return: missing `at` / `level` / `message`, `at` not parseable as
 * an ISO datetime, or a `level` other than the canonical `LogEntry`
 * levels (`info`, `warn`, `error`).
 */

import type { Option } from "../core/option.ts";
import type { CellValue, LogEntry } from "../core/dtype.ts";
import type { EncodingDecoder } from "../core/encoding-registry.ts";
import { Json } from "../core/json.ts";
import { SheetsNetwork } from "../wire/rows-network.ts";

/** The `logs.v1` wire-encoding name a logs source row declares in its metadata. */
export const LOGS_V1 = "logs.v1";

const LEVELS: readonly LogEntry["level"][] = ["info", "warn", "error"];

/** Decodes a `logs.v1` wire cell to a canonical log entry, refusing an unknown `level` (`null`). */
export const logsV1: EncodingDecoder = (wire): Option<Json | CellValue> => {
  const raw = Json.object(Json.wire(wire));
  if (raw == null) return null;
  const result = SheetsNetwork.LogsCell.safeHydrate(raw);
  if (!result.isOk) return null;
  const cell = result.value;
  const level = LEVELS.find((candidate) => candidate === cell.level);
  if (level == null) return null;
  const entry: LogEntry & { readonly source?: Option<string> } = { ...cell, level };
  return entry;
};
